<template>
  <b-modal
    v-model="modal"
    dialog-class="rounded-[.375rem]"
    body-class="!px-[2.5rem] !py-[3rem]"
    size="xl"
    hide-header
    hide-footer
    centered
    lazy
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-form
      v-if="!success"
      class="w-full flex flex-col items-center"
      @submit.prevent="submit"
    >
      <h4 class="text-center text-3xl font-semibold text-[#1D1D1D] mb-6">
        Restore Projects
      </h4>

      <p
        class="text-center text-base text-[#4A4A4A] mb-16 max-w-[620px]"
      >
        Based on your current plan, you’ve reached the maximum number of allowed projects. <br>
        Please select up to <strong class="text-black">{{ projectsCount }} {{ projectsCount === 1 ? 'project' : 'projects' }}</strong> you wish to restore.
      </p>

      <div class="flex w-full justify-start mb-4">
        <dashboard-breadcrumb :breadcrumbs="breadcrumbs" />
      </div>

      <div class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        <div
          v-for="folder in folders"
          :key="folder.id"
          class="cursor-pinter hover:bg-blue-50 transition h-[70px] relative rounded overflow-hidden w-full border border-[#E2E5EC] flex items-center justify-start"
          @click="$store.dispatch('dashboard/loadFolder', folder.id)"
        >
          <div class="h-full px-6 border-r border-[#E2E5EC] inline-flex items-center justify-center">
            <svg
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_2738_21989)">
                <path
                  d="M28.7528 32.5862H0.971348C0.332038 32.5862 -0.132859 31.9792 0.0341065 31.3616L6.34404 14.0623C6.45824 13.639 6.84245 13.3448 7.28128 13.3448H35.0627C35.702 13.3448 36 13.9556 36 14.5694L29.69 31.8687C29.5758 32.292 29.1916 32.5862 28.7528 32.5862Z"
                  fill="#94B8FC"
                />
                <path
                  d="M31.2008 7.75862H15.5172L12.4138 3.41379H1.07503C0.481655 3.41379 0 3.89482 0 4.48882V31.0096C0.0428276 31.0109 0.0856552 31.0134 0.127241 31.0159L6.34345 14.0623C6.45766 13.6384 6.84186 13.3448 7.28069 13.3448H32.2759V8.83365C32.2759 8.23965 31.7942 7.75862 31.2008 7.75862Z"
                  fill="#4F83E3"
                />
              </g>
              <defs>
                <clipPath id="clip0_2738_21989">
                  <rect
                    width="36"
                    height="36"
                    fill="white"
                  />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div class="flex flex-col items-start ml-3">
            <h3 class="text-base font-semibold text-[#44474A] mb-0">
              {{ folder.name }}
            </h3>
            <p class="text-sm text-[#9FA9BA] mb-0">
              {{ folder.countProjects }} Sites
            </p>
          </div>
        </div>
      </div>
      
      <div class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
        <div
          v-for="project in projects.data"
          :key="project.id"
          class="cursor-pinter hover:bg-blue-50 transition relative rounded overflow-hidden w-full"
          :style="{outline: selectedProjects.includes(project.id) ? '2.5px solid #007bff' : '1px solid #E2E5EC'}"
          @click="selectProject(project)"
        >
          <transition name="fade-fast">
            <div
              v-if="selectedProjects.includes(project.id)"
              class="select-none bg-black/25 absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center z-10"
            >
              <div class="shadow-sm h-10 px-3 rounded-full bg-[#4F83E3] border-2 border-white inline-flex items-center justify-center text-base font-semibold text-white">
                <svg
                  width="15"
                  height="11"
                  viewBox="0 0 15 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="mr-2"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M13.8096 1.1444C14.0635 1.39546 14.0635 1.80249 13.8096 2.05354L6.00962 9.76783C5.75578 10.0189 5.34422 10.0189 5.09038 9.76783L1.19038 5.91068C0.93654 5.65963 0.93654 5.2526 1.19038 5.00155C1.44422 4.7505 1.85578 4.7505 2.10962 5.00155L5.55 8.40412L12.8904 1.1444C13.1442 0.893353 13.5558 0.893353 13.8096 1.1444Z"
                    fill="white"
                    stroke="white"
                    stroke-width="0.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Selected
              </div>
            </div>
          </transition>

          <div
            class="relative h-[200px] w-full bg-[#c9cfd7] overflow-hidden bg-cover bg-top"
            :style="{ backgroundImage: `url(${project.screenshot})` }"
          />
          <div class="px-4 py-3">
            <p class="text-base font-semibold text-[#44474A] mb-1">
              {{ project.name }}
            </p>
            <div class="inline-flex items-center text-sm text-[#9FA9BA]">
              <span
                class="w-2 h-2 rounded-full mr-2"
                :style="{backgroundColor: project.status ? '#86C158' : '#E2E5EC'}"
              /> {{ project.status ? 'Published' : 'Not Published' }}
            </div>
          </div>
        </div>
      </div>

      <b-btn
        variant="primary"
        class="w-full max-w-[180px] h-10 !font-[600]"
        type="submit"
        :disabled="loading || !selectedProjects.length"
      >
        Restore
        <b-spinner
          v-if="loading"
          class="ml-3"
          variant="light"
          small
        />
      </b-btn>
      <a
        class="w-full max-w-[180px] mt-3 !font-[600] text-center"
        type="button"
        href="/account/billing?upgrade=1"
      >
        Upgrade Plan
      </a>
    </b-form>
    <div
      v-else
      class="w-full flex flex-col items-center"
    >
      <h4 class="text-center text-3xl font-semibold text-[#1D1D1D] mb-6">
        Success!
      </h4>

      <p
        class="text-center text-base text-[#4A4A4A] mb-16 max-w-[620px]"
      >
        Your Project entitled
        <strong 
          v-for="(project, index) in restoredProjects"
          :key="index"
          class="text-black"
        >
          {{ `“${project.name}”` + restoredProjects.length - 1 !== index ? ', ' : null }}
        </strong>
        has been restored. All other projects have been archived. To restore these projects you will need to upgrade your plan.
      </p>

      <div class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
        <transition name="fade-fast">
          <div
            v-for="project in restoredProjects"
            :key="project.id"
            class="relative rounded overflow-hidden w-full border border-[#E2E5EC]"
          >
            <div
              class="relative h-[200px] w-full bg-[#c9cfd7] overflow-hidden bg-cover bg-top"
              :style="{ backgroundImage: `url(${project.screenshot})` }"
            />
            <div class="px-4 py-3">
              <p class="text-base font-semibold text-[#44474A] mb-1">
                {{ project.name }}
              </p>
              <div class="inline-flex items-center text-sm text-[#9FA9BA]">
                <span
                  class="w-2 h-2 rounded-full mr-2"
                  :style="{backgroundColor: project.status ? '#86C158' : '#E2E5EC'}"
                /> {{ project.status ? 'Published' : 'Not Published' }}
              </div>
            </div>
          </div>
        </transition>
      </div>

      <b-btn
        variant="primary"
        class="w-full max-w-[180px] h-10 !font-[600]"
        type="button"
        @click="() => $emit('close')"
      >
        Go to Dashboard
      </b-btn>
      <a
        class="w-full max-w-[180px] mt-3 !font-[600] text-center"
        type="button"
        href="/account/billing?upgrade=1"
      >
        Upgrade Plan
      </a>
    </div>
  </b-modal>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import DashboardBreadcrumb from '@/layouts/dashboard/compontens/topbar/DashboardBreadcrumb.vue'
export default {
  name: 'RestoreProjectsModal',
  components: {DashboardBreadcrumb},
  data () {
    return {
      loading: false,
      success: false,
      selectedProjects: [],
      restoredProjects: []
    }
  },
  props: {
    value: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapState('dashboard', {
      projects: state => state.projects,
      folders: state => state.folders
    }),
    ...mapGetters({
      breadcrumbs: 'dashboard/breadcrumbs'
    }),
    modal: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    projectsCount () {
      return this.user.user_subscription?.plan?.projects_count
    }
  },
  methods: {
    ...mapActions({
      restoreProjects: 'dashboard/restoreProjects'
    }),
    selectProject (project) {
      const projectId = project.id
      const count = this.selectedProjects.length + 1
      const limited = this.projectsCount >= count
      const index = this.selectedProjects.indexOf(projectId)
      if (index !== -1) {
        this.selectedProjects.splice(index, 1)
        this.restoredProjects.splice(index, 1)
      } else if (index === -1 && limited) {
        this.selectedProjects.push(projectId)
        this.restoredProjects.push(project)
      }
    },
    async submit () {
      this.loading = true
      await this.restoreProjects({
        ids: this.selectedProjects
      })
        .then(async () => {
          this.success = true
          await this.$store.dispatch('dashboard/getDashboardData')
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>